/**
 * Manejo unificado de errores de Supabase
 */

import { handleSupabaseError, logError } from '@/utils/error-handling';
import { PostgrestError } from '@supabase/supabase-js';

// Re-exportar logError para que esté disponible cuando se importe desde este módulo
export { logError };

/**
 * Códigos de error comunes de Supabase/PostgREST
 */
export enum SupabaseErrorCode {
  NOT_FOUND = 'PGRST116',
  DUPLICATE_KEY = '23505',
  FOREIGN_KEY_VIOLATION = '23503',
  RLS_VIOLATION = '42501',
}

/**
 * Resultado de operación con error opcional
 */
export interface ServiceResult<T> {
  data: T | null;
  error: string | null;
}

/**
 * Maneja errores de Supabase y retorna un resultado estructurado
 */
export function handleSupabaseServiceError<T>(
  error: unknown,
  defaultMessage: string = 'Error al realizar la operación'
): ServiceResult<T> {
  if (!error) {
    return { data: null, error: defaultMessage };
  }

  const appError = handleSupabaseError(error);
  logError(appError, 'SupabaseService', true);

  // Manejar códigos específicos de Supabase
  if (error && typeof error === 'object' && 'code' in error) {
    const supabaseError = error as PostgrestError;
    
    switch (supabaseError.code) {
      case SupabaseErrorCode.NOT_FOUND:
        return { data: null, error: null }; // No encontrado no es necesariamente un error
      
      case SupabaseErrorCode.DUPLICATE_KEY:
        return {
          data: null,
          error: 'Ya existe un registro con estos datos',
        };
      
      case SupabaseErrorCode.FOREIGN_KEY_VIOLATION:
        return {
          data: null,
          error: 'Referencia inválida. El recurso relacionado no existe.',
        };
      
      case SupabaseErrorCode.RLS_VIOLATION:
        return {
          data: null,
          error: 'No tienes permisos para realizar esta operación',
        };
    }
  }

  return {
    data: null,
    error: appError.message || defaultMessage,
  };
}

/**
 * Wrapper para operaciones de Supabase que maneja errores automáticamente
 */
export async function safeSupabaseOperation<T>(
  operation: () => Promise<{ data: T | null; error: PostgrestError | null }>,
  defaultMessage: string = 'Error al realizar la operación'
): Promise<ServiceResult<T>> {
  try {
    const result = await operation();
    
    if (result.error) {
      return handleSupabaseServiceError(result.error, defaultMessage);
    }
    
    return {
      data: result.data,
      error: null,
    };
  } catch (error) {
    return handleSupabaseServiceError(error, defaultMessage);
  }
}

/**
 * Verifica si un error es "no encontrado" (no es necesariamente un error)
 */
export function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  
  if ('code' in error) {
    return (error as PostgrestError).code === SupabaseErrorCode.NOT_FOUND;
  }
  
  if ('message' in error) {
    const message = (error as Error).message.toLowerCase();
    return message.includes('not found') || message.includes('pgrst116');
  }
  
  return false;
}

/**
 * Verifica si un error es de duplicado
 */
export function isDuplicateError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return false;
  }
  
  const supabaseError = error as PostgrestError;
  return (
    supabaseError.code === SupabaseErrorCode.DUPLICATE_KEY ||
    supabaseError.message?.toLowerCase().includes('duplicate') ||
    supabaseError.message?.toLowerCase().includes('already exists')
  );
}
